import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { YStack, XStack, Text, Spinner } from "tamagui";
import { RiArrowRightSLine } from "react-icons/ri";

import SkinV2Screen from "@components/layout/SkinV2Screen";
import { v2 } from "@screen/Home/pages/GasShop/skin";
import { colors } from "@styles/colors";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { buildXContextHeader } from "@utils/api/headers";
import { IdentityContext } from "@utils/store/user/useUser";

import SectionLabel from "./components/ui/SectionLabel";
import ListContainer from "./components/ui/ListContainer";
import InfoNote from "./components/ui/InfoNote";
import { listBills, type BillListItem, type ListBillsRes } from "./API/handleBillsAPI";
import {
  billMonthLabel,
  billPeriodLabel,
  billStatusMeta,
  billTypeLabel,
  money,
  ymdSlash,
} from "./bills/billDisplay";

const PAGE_LIMIT = 20;

interface MonthGroup {
  key: string;
  items: BillListItem[];
}

/** 依帳期迄日的月份分組；後端已依帳期新到舊排序，保留首次出現順序 */
const groupByMonth = (items: BillListItem[]): MonthGroup[] => {
  const order: string[] = [];
  const map = new Map<string, BillListItem[]>();
  items.forEach((it) => {
    const key = billMonthLabel(it.period_end);
    if (!map.has(key)) {
      order.push(key);
      map.set(key, []);
    }
    map.get(key)!.push(it);
  });
  return order.map((key) => ({ key, items: map.get(key)! }));
};

/**
 * 帳戶帳單列表：瓦斯行開立的月結／瓦斯表帳單（GET /app-api/bills，需 X-Context）。
 * 頂端是所有未付清帳單的待繳合計（後端加總，不受分頁影響），下面依帳期月份分組。
 */
export const AccountBills = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { contextId } = useParams();
  const { cusJwtToken } = useCusAuth();

  const ctx = (location.state as { context?: IdentityContext } | null)?.context ?? null;
  const xContext = buildXContextHeader(ctx) ?? (contextId ? `context:${contextId}` : "");

  const [items, setItems] = useState<BillListItem[]>([]);
  const [summary, setSummary] = useState<ListBillsRes["summary"] | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [failed, setFailed] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [cursor, setCursor] = useState<string | null>(null);

  useEffect(() => {
    if (!cusJwtToken || !xContext) {
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    setFailed(false);
    listBills({ jwtToken: cusJwtToken, xContext, params: { limit: PAGE_LIMIT } })
      .then((res) => {
        if (!active) return;
        if (res.state === "success") {
          setItems(res.data.items);
          setSummary(res.data.summary);
          setCursor(res.data.next_cursor);
        } else {
          console.log("bills err", res.error);
          setFailed(true);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [cusJwtToken, xContext]);

  const loadMore = () => {
    if (!cusJwtToken || !xContext || !cursor || loadingMore) return;
    setLoadingMore(true);
    listBills({ jwtToken: cusJwtToken, xContext, params: { limit: PAGE_LIMIT, cursor } })
      .then((res) => {
        if (res.state === "success") {
          setItems((prev) => [...prev, ...res.data.items]);
          setCursor(res.data.next_cursor);
        } else {
          console.log("bills load more err", res.error);
        }
      })
      .finally(() => setLoadingMore(false));
  };

  const back = () => navigate(`/account/detail/${contextId}`, { state: { context: ctx } });
  const openBill = (bill: BillListItem) =>
    navigate(`/account/detail/${contextId}/bills/${bill.unified_bill_id}`, { state: { context: ctx } });

  const vendor = ctx?.primary_supplier?.supplier_name ?? "瓦斯行";
  const groups = groupByMonth(items);

  const subHeader = (
    <YStack mb={16} gap={4}>
      <Text color={v2.hazeSub} fontSize={13}>
        {vendor}
      </Text>
      {summary && summary.unpaid_count > 0 && (
        <XStack ai="baseline" gap={8}>
          <Text color={v2.hazeInk} fontSize={13} fontWeight="700">
            待繳 {summary.unpaid_count} 張
          </Text>
          <Text
            color={v2.hazeInk}
            fontSize={22}
            fontWeight="800"
            style={{ fontVariantNumeric: "tabular-nums" }}>
            {money(summary.unpaid_total)}
          </Text>
        </XStack>
      )}
    </YStack>
  );

  return (
    <SkinV2Screen title="帳單" onBack={back} subHeader={subHeader} band="short">
      <YStack className="gas-stagger-kids" gap="$3.5">
        {loading ? (
          <YStack f={1} ai="center" jc="center" py="$8">
            <Spinner size="large" color={colors.primary} />
          </YStack>
        ) : failed ? (
          <YStack f={1} ai="center" jc="center" py="$8">
            <Text fontSize={14} color={v2.body}>
              帳單暫時載入失敗，請稍後再試
            </Text>
          </YStack>
        ) : items.length === 0 ? (
          <YStack f={1} ai="center" jc="center" py="$8" gap="$2">
            <Text fontSize={14} color={v2.body}>
              目前沒有帳單
            </Text>
            <Text fontSize={12} color={v2.sub} ta="center">
              月結或瓦斯表計費的客戶，瓦斯行開立帳單後會出現在這裡
            </Text>
          </YStack>
        ) : (
          <>
            {groups.map((g) => (
              <YStack key={g.key}>
                <SectionLabel title={g.key} />
                <ListContainer>
                  {g.items.map((bill, i) => {
                    const status = billStatusMeta(bill);
                    const settled = bill.status === "paid";
                    return (
                      <XStack
                        key={bill.unified_bill_id}
                        ai="center"
                        gap="$2.5"
                        px="$3.5"
                        py="$3"
                        bbw={i === g.items.length - 1 ? 0 : 1}
                        bbc={v2.divider}
                        cursor="pointer"
                        pressStyle={{ opacity: 0.7 }}
                        onPress={() => openBill(bill)}>
                        <YStack f={1} gap="$1">
                          <XStack ai="center" gap="$2">
                            <Text fontSize={14} fontWeight="700" color={v2.title} numberOfLines={1}>
                              {billTypeLabel(bill.bill_type)}
                            </Text>
                            <Text
                              fontSize={11}
                              fontWeight="700"
                              color={status.fg}
                              bg={status.bg}
                              px={6}
                              py={1}
                              br={999}>
                              {status.label}
                            </Text>
                          </XStack>
                          <Text fontSize={11} color={v2.sub} numberOfLines={1}>
                            {billPeriodLabel(bill.period_start, bill.period_end)}
                            {!settled && bill.payment_deadline ? ` · 繳費期限 ${ymdSlash(bill.payment_deadline)}` : ""}
                          </Text>
                        </YStack>
                        <YStack ai="flex-end" gap="$1" flexShrink={0}>
                          <Text
                            fontSize={15}
                            fontWeight="800"
                            color={v2.title}
                            style={{ fontVariantNumeric: "tabular-nums" }}>
                            {money(settled ? bill.total_amount : bill.outstanding_amount)}
                          </Text>
                          <Text fontSize={11} color={v2.sub}>
                            {settled ? "帳單金額" : "待繳"}
                          </Text>
                        </YStack>
                        <RiArrowRightSLine size={16} color={v2.sub} />
                      </XStack>
                    );
                  })}
                </ListContainer>
              </YStack>
            ))}

            {cursor && (
              <XStack
                ai="center"
                jc="center"
                gap="$1.5"
                bg={v2.card}
                br={999}
                py="$3"
                cursor={loadingMore ? "default" : "pointer"}
                opacity={loadingMore ? 0.6 : 1}
                pressStyle={{ opacity: 0.7 }}
                style={{ boxShadow: "0 2px 10px -4px rgba(21, 101, 192, 0.10)" }}
                onPress={loadMore}>
                {loadingMore && <Spinner size="small" color={colors.primary} />}
                <Text fontSize={13} fontWeight="700" color={v2.primary}>
                  {loadingMore ? "載入中…" : "載入更多"}
                </Text>
              </XStack>
            )}

            <InfoNote tone="primary">待繳金額不含上期欠款；實際應付以帳單內容為準。</InfoNote>
          </>
        )}
      </YStack>
    </SkinV2Screen>
  );
};

export default AccountBills;
